import { useEffect, useState } from 'react'
import { Search, Users, UserX } from 'lucide-react'
import { useHRSuggestionStore } from '../../stores/hrSuggestionStore'
import { getFullName } from '../../utils/orgHierarchyUtils'
import type { PotentialHRPartner } from '../../types/orgHierarchy'
import { Spinner } from '@t-one-internal/design-system'
import apiClient from '../../api/apiClient'
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@t-one-internal/design-system/lib/components/ui/popover'

interface SmartHRDropdownProps {
    scopeInstanceId?: string
    employeeId?: string
    onSelectHRPartner: (hrPartner: PotentialHRPartner | null) => void
    selectedHRPartnerId?: string | null
    className?: string
    allowClear?: boolean
}

export const SmartHRDropdown = ({
    scopeInstanceId,
    employeeId,
    onSelectHRPartner,
    selectedHRPartnerId,
    className = '',
    allowClear = false,
}: SmartHRDropdownProps) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    // Fallback: if selectedHRPartnerId isn't found in the groups list, resolve from API
    const [resolvedSelectedHRPartner, setResolvedSelectedHRPartner] = useState<PotentialHRPartner | null>(null)

    const { groupedHRPartners, suggestedHRPartner, isLoading, fetchPotentialHRPartners } =
        useHRSuggestionStore()

    useEffect(() => {
        // Fetch potential HR partners - works with or without scopeInstanceId
        fetchPotentialHRPartners({
            scope_instance_id: scopeInstanceId || '',
            employee_id: employeeId,
            grouped: true,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeInstanceId, employeeId])

    const handleSelectHRPartner = (hrPartner: PotentialHRPartner | null) => {
        onSelectHRPartner(hrPartner)
        setIsOpen(false)
        setSearchQuery('')
    }

    const handleClearSelection = () => {
        onSelectHRPartner(null)
        setIsOpen(false)
        setSearchQuery('')
    }

    const filterHRPartners = (hrPartners: PotentialHRPartner[]) => {
        if (!searchQuery.trim()) {
            return hrPartners
        }

        const query = searchQuery.toLowerCase()
        return hrPartners.filter(
            (hrPartner) =>
                hrPartner.fname?.toLowerCase().includes(query) ||
                hrPartner.lname?.toLowerCase().includes(query) ||
                hrPartner.email?.toLowerCase().includes(query) ||
                hrPartner.job_title?.toLowerCase().includes(query)
        )
    }

    const selectedHRPartnerFromList = groupedHRPartners?.groups
        .flatMap((group) => group.hrPartners)
        .find((hr) => hr.user_uuid === selectedHRPartnerId)

    // Fallback: resolve selected HR partner from API if not found in the groups list
    useEffect(() => {
        if (selectedHRPartnerId && !selectedHRPartnerFromList && !isLoading) {
            let cancelled = false
            const resolveHRPartner = async () => {
                try {
                    const { data } = await apiClient.get<{ user?: any }>(`/admin/users/${selectedHRPartnerId}`)
                    if (cancelled) return
                    const user = (data as any).user || data
                    setResolvedSelectedHRPartner({
                        user_id: 0,
                        user_uuid: selectedHRPartnerId,
                        fname: user.first_name || user.fname || '',
                        lname: user.last_name || user.lname || '',
                        email: user.email || '',
                        job_title: user.job_title || null,
                        scope_name: null,
                        scope_instance_id: '',
                        priority_group: 4,
                        is_primary: false,
                    })
                } catch (err) {
                    console.error('Failed to resolve selected HR partner:', err)
                    if (!cancelled) setResolvedSelectedHRPartner(null)
                }
            }
            resolveHRPartner()
            return () => { cancelled = true }
        } else if (!selectedHRPartnerId) {
            setResolvedSelectedHRPartner(null)
        }
    }, [selectedHRPartnerId, selectedHRPartnerFromList, isLoading])

    const selectedHRPartner = selectedHRPartnerFromList || resolvedSelectedHRPartner

    return (
        <div className={className}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        className="w-full h-9 px-3 text-left bg-white border border-slate-200 rounded-md hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent transition-colors flex items-center justify-between"
                    >
                        {selectedHRPartner ? (
                            <div className="flex items-center gap-2 overflow-hidden min-w-0">
                                <div className="w-5 h-5 rounded-full bg-pink-50 flex items-center justify-center flex-shrink-0">
                                    <Users className="w-3 h-3 text-pink-600" />
                                </div>
                                <span className="text-sm text-slate-700 truncate min-w-0">
                                    {getFullName(selectedHRPartner.fname, selectedHRPartner.lname)}
                                </span>
                            </div>
                        ) : (
                            <span className="text-sm text-slate-500">Select HR partner...</span>
                        )}
                        <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2" />
                    </button>
                </PopoverTrigger>

                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <div className="flex flex-col max-h-72 overflow-hidden bg-white rounded-lg shadow-lg border border-slate-200">
                        {/* Search */}
                        <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search..."
                                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-pink-500 focus:border-pink-500 transition-all placeholder:text-slate-400"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {/* Not Assigned Option */}
                        {allowClear && !searchQuery && (
                            <button
                                type="button"
                                onClick={handleClearSelection}
                                className={`w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 border-b border-slate-100 ${!selectedHRPartnerId ? 'bg-slate-50' : ''}`}
                            >
                                <UserX className="w-4 h-4 text-slate-400" />
                                <span className="text-sm text-slate-600">Not Assigned</span>
                            </button>
                        )}

                        {/* Suggested HR Partner */}
                        {suggestedHRPartner && !searchQuery && (
                            <div className="border-b border-slate-100">
                                <div className="px-3 py-1.5 text-[10px] font-semibold text-pink-500 uppercase tracking-wider bg-pink-50/50">
                                    Suggested
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleSelectHRPartner(suggestedHRPartner)}
                                    className="w-full px-3 py-2 text-left hover:bg-pink-50/50 transition-colors flex items-center gap-2"
                                >
                                    <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                                        <Users className="w-3.5 h-3.5 text-pink-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm font-medium text-slate-800 truncate block">
                                            {getFullName(suggestedHRPartner.fname, suggestedHRPartner.lname)}
                                        </span>
                                        <span className="text-xs text-slate-500 truncate block">
                                            {suggestedHRPartner.job_title || 'No title'}
                                        </span>
                                    </div>
                                </button>
                            </div>
                        )}

                        {/* HR Partner List */}
                        <div className="overflow-y-auto flex-1">
                            {isLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Spinner size="sm" />
                                </div>
                            ) : groupedHRPartners ? (
                                <div>
                                    {groupedHRPartners.groups.map((group) => {
                                        const filteredHRPartners = filterHRPartners(group.hrPartners)

                                        if (filteredHRPartners.length === 0) {
                                            return null
                                        }

                                        return (
                                            <div key={group.priority}>
                                                <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 sticky top-0">
                                                    {group.label}
                                                </div>
                                                <div>
                                                    {filteredHRPartners.map((hrPartner) => (
                                                        <button
                                                            key={hrPartner.user_uuid}
                                                            type="button"
                                                            onClick={() => handleSelectHRPartner(hrPartner)}
                                                            className={`w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2 ${selectedHRPartnerId === hrPartner.user_uuid ? 'bg-pink-50' : ''}`}
                                                        >
                                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                                <Users className="w-3.5 h-3.5 text-slate-400" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className={`text-sm truncate ${selectedHRPartnerId === hrPartner.user_uuid ? 'text-pink-900 font-medium' : 'text-slate-700'}`}>
                                                                        {getFullName(hrPartner.fname, hrPartner.lname)}
                                                                    </span>
                                                                    {hrPartner.is_primary && (
                                                                        <span className="text-[9px] font-medium text-pink-600 bg-pink-50 px-1 rounded">
                                                                            Primary
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="text-xs text-slate-400 truncate block">
                                                                    {hrPartner.job_title || 'No title'}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {groupedHRPartners.groups.every((group) => filterHRPartners(group.hrPartners).length === 0) && (
                                        <div className="px-3 py-6 text-center">
                                            <p className="text-sm text-slate-500">No HR partners found</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="px-3 py-6 text-center text-slate-400 text-sm">
                                    No HR partners available
                                </div>
                            )}
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}
